import { useCallback, useEffect, useState } from 'react';
import { useSpotifyClient } from '../spotify/useSpotifyClient.ts';
import { getDevices } from '../spotify/api.ts';
import { resolveTargetDevice, toPreference, type DeviceRole } from '../spotify/deviceResolver.ts';
import type { SpotifyDevice } from '../spotify/types.ts';
import {
  loadSettings,
  saveSettings,
  type DevicePreference,
  type Settings,
} from '../data/settings.ts';

function roleLabel(role: DeviceRole): string {
  if (role === 'primary') return 'Standardgerät';
  if (role === 'fallback') return 'Ersatzgerät';
  return 'gerade aktiv';
}

function matchesPreference(
  device: { id: string | null; name: string },
  preference: DevicePreference | null,
): boolean {
  if (!preference) return false;
  if (preference.id && device.id === preference.id) return true;
  return device.name.trim().toLowerCase() === preference.name.trim().toLowerCase();
}

/**
 * Auswahl von Standard- und Ersatzgeraet.
 *
 * Wird sowohl im Elternmenue als auch im Diagnose-Bildschirm verwendet,
 * damit es nur eine Stelle mit dieser Logik gibt.
 */
export function DeviceSettings({ onMessage }: { onMessage?: (text: string) => void }) {
  const client = useSpotifyClient();
  const [devices, setDevices] = useState<SpotifyDevice[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [settings, setSettings] = useState<Settings>(() => loadSettings());
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const list = await getDevices(client);
      setDevices(list);
      setSelectedId((current) => {
        if (current && list.some((device) => device.id === current)) return current;
        return (list.find((device) => device.is_active) ?? list[0])?.id ?? '';
      });
    } catch (err) {
      onMessage?.(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [client, onMessage]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const selected = devices.find((device) => device.id === selectedId) ?? null;
  const resolved = resolveTargetDevice(devices, settings.primaryDevice, settings.fallbackDevice);

  const assign = (role: 'primary' | 'fallback') => {
    if (!selected) return;
    const next: Settings = {
      ...settings,
      [role === 'primary' ? 'primaryDevice' : 'fallbackDevice']: toPreference(selected),
    };
    saveSettings(next);
    setSettings(next);
    onMessage?.(
      `„${selected.name}" als ${role === 'primary' ? 'Standardgerät' : 'Ersatzgerät'} gemerkt.`,
    );
  };

  const clear = (role: 'primary' | 'fallback') => {
    const next: Settings = {
      ...settings,
      [role === 'primary' ? 'primaryDevice' : 'fallbackDevice']: null,
    };
    saveSettings(next);
    setSettings(next);
    onMessage?.(`${role === 'primary' ? 'Standardgerät' : 'Ersatzgerät'} entfernt.`);
  };

  return (
    <>
      <p className="muted">
        Das iPad ist nur Fernbedienung. Abgespielt wird auf dem Lautsprecher im Kinderzimmer.
        Reihenfolge: Standardgerät → Ersatzgerät → irgendein aktives Gerät → „Hol einen
        Erwachsenen"-Bildschirm.
      </p>

      <ul className="facts">
        <li>
          <span>Standardgerät</span>
          <strong>{settings.primaryDevice?.name ?? 'nicht festgelegt'}</strong>
        </li>
        <li>
          <span>Ersatzgerät</span>
          <strong>{settings.fallbackDevice?.name ?? 'nicht festgelegt'}</strong>
        </li>
      </ul>

      <div className="actions">
        <button type="button" onClick={() => void refresh()} disabled={busy}>
          Geräte aktualisieren
        </button>
      </div>

      {devices.length === 0 ? (
        <p className="muted">
          Keine Geräte gefunden. Taucht ein Echo nicht auf, einmal per Sprachbefehl Spotify darauf
          starten – danach bleibt er in der Liste.
        </p>
      ) : (
        <ul className="devices">
          {devices.map((device) => (
            <li key={device.id ?? device.name}>
              <label>
                <input
                  type="radio"
                  name="spotibox-device"
                  checked={selectedId === device.id}
                  onChange={() => setSelectedId(device.id ?? '')}
                  disabled={!device.id}
                />
                <strong>{device.name}</strong>
                <span className="muted">
                  {device.type}
                  {device.is_active ? ' · aktiv' : ''}
                  {device.is_restricted ? ' · nicht fernsteuerbar' : ''}
                  {matchesPreference(device, settings.primaryDevice) ? ' · Standard' : ''}
                  {matchesPreference(device, settings.fallbackDevice) ? ' · Ersatz' : ''}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      <div className="actions">
        <button type="button" disabled={!selected || busy} onClick={() => assign('primary')}>
          Als Standardgerät
        </button>
        <button type="button" disabled={!selected || busy} onClick={() => assign('fallback')}>
          Als Ersatzgerät
        </button>
        <button
          type="button"
          disabled={!settings.fallbackDevice || busy}
          onClick={() => clear('fallback')}
        >
          Ersatzgerät entfernen
        </button>
      </div>

      <p className={resolved ? 'pill pill--ok' : 'pill pill--warn'}>
        <span aria-hidden="true">{resolved ? '✓' : '!'}</span>{' '}
        {resolved
          ? `Spotibox spielt auf: ${resolved.device.name} (${roleLabel(resolved.role)})`
          : 'Kein steuerbares Gerät – das Kind bekäme den „Hol einen Erwachsenen"-Bildschirm'}
      </p>
    </>
  );
}
